import React from 'react';

const COLOR_LEGEND = [
  { color: '#00C853', label: '> 95%',  desc: 'Excellent — very high identity to reference' },
  { color: '#64DD17', label: '90–95%', desc: 'Good — strong alignment' },
  { color: '#AEEA00', label: '85–90%', desc: 'Moderate — acceptable identity' },
  { color: '#FFD600', label: '80–85%', desc: 'Low-moderate — possible divergence' },
  { color: '#FFAB00', label: '75–80%', desc: 'Low — significant divergence' },
  { color: '#FF6D00', label: '< 75%',  desc: 'Poor — highly divergent or possible misidentification' },
];

const SECTIONS = [
  {
    icon: '🌍',
    title: 'Overview',
    content: `The Map Chart plots ReadHit sequence data onto a world map by extracting the country of origin from the sseqid field. Each country is colour-coded by the average percent identity of all sequences associated with that country. Hovering over a country displays a tooltip with detailed statistics and sample sequence IDs.`,
  },
  {
    icon: '📍',
    title: 'Country Detection',
    content: `Country codes are extracted from sseqid strings using pattern matching for ISO 3-letter codes (e.g. BRA, PAK, UGA, YEM). If a country code cannot be matched, the sequence is excluded from the map. Only sequences with a valid country code and coordinates are displayed.`,
  },
  {
    icon: '🎨',
    title: 'Color Scale — Percent Identity',
    colorLegend: true,
  },
  {
    icon: '🔍',
    title: 'Filters',
    rows: [
      ['Percent Identity', 'Dual-range slider to restrict sequences by minimum and maximum % identity.'],
      ['Sequence Length',  'Dual-range slider to restrict sequences by alignment or query length (bp).'],
      ['Sequence Type',    'Filter by the prefix of the sseqid (e.g. VP1, VP2, VP3, 2A, 3D). Multi-select checkboxes.'],
      ['Virus Type',       'Filter by virus type extracted from sseqid (e.g. PV1, PV2, PV3). Multi-select checkboxes.'],
    ],
  },
  {
    icon: '📊',
    title: 'Data Summary Panel',
    rows: [
      ['Total Sequences', 'Count of sequences passing all active filters that have a valid country code.'],
      ['Countries',       'Number of distinct countries present in the filtered data.'],
      ['Avg Identity',    'Average percent identity across all filtered sequences with a country code.'],
    ],
  },
  {
    icon: '🖱️',
    title: 'Tooltip',
    content: `Hover over any coloured country to see: country name and ISO code, total sequence count, average percent identity, and up to 5 sample sseqid strings from that country.`,
  },
  {
    icon: '↺',
    title: 'Reset Filters',
    content: `Click Reset Filters to restore all sliders to their full range and re-select all sequence types, reference IDs, and virus types.`,
  },
  {
    icon: '⚠️',
    title: 'Performance',
    content: `The map automatically limits processing to the first 150,000 rows. A warning banner appears if the dataset exceeds this limit. Applying tighter filters reduces memory usage and improves rendering performance.`,
  },
];

export default function UserGuidMapChart({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="rh-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal-box rh-guide-modal" role="dialog" aria-modal="true" aria-labelledby="guide-title-map">

        <div className="rh-modal-header">
          <div>
            <div className="rh-modal-title" id="guide-title-map">Map Chart — User Guide</div>
            <div className="rh-modal-subtitle">How to read and filter the world map visualization</div>
          </div>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="rh-guide-body">
          {SECTIONS.map((sec, i) => (
            <div key={i} className="rh-guide-section">
              <div className="rh-guide-section-title">
                <span style={{ fontSize: 14, marginRight: 4 }}>{sec.icon}</span>
                {sec.title}
              </div>

              {sec.content && <p className="rh-guide-text">{sec.content}</p>}

              {sec.rows && (
                <table className="rh-guide-table">
                  <tbody>
                    {sec.rows.map(([term, desc]) => (
                      <tr key={term}>
                        <td className="rh-guide-term">{term}</td>
                        <td className="rh-guide-desc">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {sec.colorLegend && (
                <div className="rh-guide-color-legend">
                  {COLOR_LEGEND.map(({ color, label, desc }) => (
                    <div key={label} className="rh-guide-color-row">
                      <span className="rh-guide-color-swatch" style={{ background: color }} />
                      <span className="rh-guide-color-label">{label}</span>
                      <span className="rh-guide-color-desc">{desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
